import React, { useState, useEffect, useCallback } from 'react';
import './FinancePage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const fmtTZS = (n) => {
  if (!n && n !== 0) return '—';
  const num = Number(n);
  if (num >= 1_000_000) return `TZS ${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000)     return `TZS ${(num / 1_000).toFixed(0)}K`;
  return `TZS ${num.toLocaleString()}`;
};

const FinancePage = () => {
  const [balance, setBalance]       = useState(null);
  const [sellers, setSellers]       = useState([]);
  const [drivers, setDrivers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [toast, setToast]           = useState(null);

  // Payout Operations
  const [eligible, setEligible]         = useState({ count: 0, total: 0, sellers: [] });
  const [previewing, setPreviewing]     = useState(false);
  const [preview, setPreview]           = useState(null); // {would_pay, total, sellers}
  const [syncingPayouts, setSyncingPayouts] = useState(false);
  const [syncResult, setSyncResult]     = useState(null); // {checked, changed, failed, restored, waiting, results}
  const [detailRef, setDetailRef]       = useState(null);
  const [detail, setDetail]             = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Config form state
  const [sellerType,  setSellerType]  = useState('percentage');
  const [sellerValue, setSellerValue] = useState('');
  const [driverType,  setDriverType]  = useState('percentage');
  const [driverValue, setDriverValue] = useState('');
  const [minPayout,   setMinPayout]   = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [balData, sellData, drvData, cfgData, eligData] = await Promise.allSettled([
        apiFetch('/api/admin/balance'),
        apiFetch('/api/admin/sellers/balances'),
        apiFetch('/api/admin/drivers/balances'),
        apiFetch('/api/admin/config/payment'),
        apiFetch('/api/admin/payouts/eligible'),
      ]);

      if (balData.status  === 'fulfilled') setBalance(balData.value?.balances || balData.value);
      if (sellData.status === 'fulfilled') setSellers(sellData.value?.sellers || []);
      if (drvData.status  === 'fulfilled') setDrivers(drvData.value?.drivers  || []);
      if (eligData.status === 'fulfilled') setEligible({
        count: eligData.value?.count || 0,
        total: eligData.value?.total || 0,
        sellers: eligData.value?.sellers || [],
      });
      if (cfgData.status  === 'fulfilled') {
        const c = cfgData.value;
        setSellerType(c.seller_commission_type   || 'percentage');
        setSellerValue(String(c.seller_commission_value ?? ''));
        setDriverType(c.driver_commission_type   || 'percentage');
        setDriverValue(String(c.driver_commission_value ?? ''));
        setMinPayout(String(c.min_payout_amount  ?? ''));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/admin/config/payment', {
        method: 'PUT',
        body: JSON.stringify({
          seller_commission_type:  sellerType,
          seller_commission_value: Number(sellerValue),
          driver_commission_type:  driverType,
          driver_commission_value: Number(driverValue),
          min_payout_amount:       Number(minPayout),
        }),
      });
      showToast('Commission config saved.');
      load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const previewPayoutRun = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await apiFetch('/api/admin/payouts/run', {
        method: 'POST',
        body: JSON.stringify({ dry_run: true }),
      });
      setPreview(res);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const syncPayouts = async () => {
    setSyncingPayouts(true);
    setSyncResult(null);
    try {
      const res = await apiFetch('/api/admin/payouts/sync', { method: 'POST' });
      setSyncResult(res);
      if (res.skipped === 'quota') {
        showToast('Sync skipped — ClickPesa daily quota is exhausted.', 'error');
      } else {
        showToast(
          `Checked ${res.checked ?? 0} · Changed ${res.changed ?? 0} · Restored ${res.restored ?? 0} · Failed ${res.failed ?? 0} · Waiting ${res.waiting ?? 0}`
        );
      }
      load();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSyncingPayouts(false);
    }
  };

  const viewPayoutDetail = async (reference) => {
    setDetailRef(reference);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await apiFetch(`/api/admin/payouts/${reference}`);
      setDetail(res);
    } catch (e) {
      showToast(e.message, 'error');
      setDetailRef(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalSellersBalance = sellers.reduce((s, r) => s + Number(r.available || 0), 0);
  const totalDriversBalance = drivers.reduce((s, r) => s + Number(r.available || 0), 0);

  return (
    <div className="fin-page">
      {toast && <div className={`fin-toast fin-toast--${toast.type}`}>{toast.msg}</div>}

      {detailRef && (
        <div className="fin-overlay" onClick={() => setDetailRef(null)}>
          <div className="fin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="fin-modal-title">Payout Detail</h3>
            <p className="fin-modal-sub">Reference: <code className="fin-code">{detailRef}</code></p>
            {detailLoading && <div className="fin-loading"><div className="mp-spinner" /> Loading…</div>}
            {!detailLoading && detail && (
              <div className="fin-detail-grid">
                <div className="fin-detail-block">
                  <h4 className="fin-detail-block-title">ClickPesa Record</h4>
                  <pre className="fin-detail-pre">{JSON.stringify(detail.clickpesa, null, 2)}</pre>
                </div>
                <div className="fin-detail-block">
                  <h4 className="fin-detail-block-title">Our Record</h4>
                  <pre className="fin-detail-pre">{JSON.stringify(detail.ours, null, 2)}</pre>
                </div>
              </div>
            )}
            <div className="fin-modal-actions">
              <button className="fin-btn fin-btn--ghost" onClick={() => setDetailRef(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="fin-header">
        <div>
          <h1 className="dashboard-title">Finance</h1>
          <p className="dashboard-subtitle">Platform balance, commission settings, and seller/driver earnings.</p>
        </div>
        <button className="fin-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="fin-error">{error} <button onClick={load}>Retry</button></div>}

      {/* Platform balance */}
      {balance && (
        <div className="fin-section">
          <h2 className="fin-section-title">Platform Account (ClickPesa)</h2>
          <div className="fin-balance-grid">
            {Array.isArray(balance)
              ? balance.map((b, i) => (
                  <div key={i} className="fin-balance-card">
                    <span className="fin-balance-label">{b.currency || b.type || `Account ${i + 1}`}</span>
                    <span className="fin-balance-value">{fmtTZS(b.amount ?? b.balance ?? b.value)}</span>
                  </div>
                ))
              : Object.entries(balance).map(([k, v]) => (
                  <div key={k} className="fin-balance-card">
                    <span className="fin-balance-label">{k}</span>
                    <span className="fin-balance-value">{typeof v === 'number' ? fmtTZS(v) : String(v)}</span>
                  </div>
                ))
            }
          </div>
        </div>
      )}

      {/* Earnings summary */}
      <div className="fin-row2">
        <div className="fin-summary-card">
          <div className="fin-summary-icon" style={{ background: '#ecfdf5' }}>💰</div>
          <div>
            <div className="fin-summary-label">Total Seller Earnings</div>
            <div className="fin-summary-val">{fmtTZS(totalSellersBalance)}</div>
            <div className="fin-summary-sub">{sellers.length} sellers</div>
          </div>
        </div>
        <div className="fin-summary-card">
          <div className="fin-summary-icon" style={{ background: '#eff6ff' }}>🚚</div>
          <div>
            <div className="fin-summary-label">Total Driver Earnings</div>
            <div className="fin-summary-val">{fmtTZS(totalDriversBalance)}</div>
            <div className="fin-summary-sub">{drivers.length} drivers</div>
          </div>
        </div>
      </div>

      {/* Commission config */}
      <div className="fin-section">
        <h2 className="fin-section-title">Commission Settings</h2>
        <form className="fin-config-form" onSubmit={saveConfig}>
          <div className="fin-config-grid">
            <div className="fin-config-group">
              <label className="fin-label">Seller Commission Type</label>
              <select className="fin-select" value={sellerType} onChange={(e) => setSellerType(e.target.value)}>
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat (TZS)</option>
              </select>
            </div>
            <div className="fin-config-group">
              <label className="fin-label">
                Seller Commission {sellerType === 'percentage' ? '(%)' : '(TZS)'}
              </label>
              <input className="fin-input" type="number" min="0" step="0.01"
                value={sellerValue} onChange={(e) => setSellerValue(e.target.value)} />
            </div>
            <div className="fin-config-group">
              <label className="fin-label">Driver Commission Type</label>
              <select className="fin-select" value={driverType} onChange={(e) => setDriverType(e.target.value)}>
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat (TZS)</option>
              </select>
            </div>
            <div className="fin-config-group">
              <label className="fin-label">
                Driver Commission {driverType === 'percentage' ? '(%)' : '(TZS)'}
              </label>
              <input className="fin-input" type="number" min="0" step="0.01"
                value={driverValue} onChange={(e) => setDriverValue(e.target.value)} />
            </div>
            <div className="fin-config-group">
              <label className="fin-label">Minimum Payout (TZS)</label>
              <input className="fin-input" type="number" min="0"
                value={minPayout} onChange={(e) => setMinPayout(e.target.value)} />
            </div>
          </div>
          <button type="submit" className="fin-save-btn" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Config'}
          </button>
        </form>
      </div>

      {/* Sellers table */}
      {sellers.length > 0 && (
        <div className="fin-section">
          <h2 className="fin-section-title">Seller Balances</h2>
          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead><tr><th>Seller ID</th><th>Available</th><th>Pending</th><th>Lifetime</th></tr></thead>
              <tbody>
                {sellers.map((s, i) => (
                  <tr key={s.seller_id || i}>
                    <td><code className="fin-code">{(s.seller_id || '—').slice(0, 14)}…</code></td>
                    <td className="fin-green">{fmtTZS(s.available)}</td>
                    <td className="fin-amber">{fmtTZS(s.pending)}</td>
                    <td>{fmtTZS(s.total_earned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Drivers table */}
      {drivers.length > 0 && (
        <div className="fin-section">
          <h2 className="fin-section-title">Driver Balances</h2>
          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead><tr><th>Driver ID</th><th>Available</th><th>Pending</th><th>Lifetime</th></tr></thead>
              <tbody>
                {drivers.map((d, i) => (
                  <tr key={d.driver_id || i}>
                    <td><code className="fin-code">{(d.driver_id || '—').slice(0, 14)}…</code></td>
                    <td className="fin-green">{fmtTZS(d.available)}</td>
                    <td className="fin-amber">{fmtTZS(d.pending)}</td>
                    <td>{fmtTZS(d.total_earned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payout Operations */}
      <div className="fin-section">
        <h2 className="fin-section-title">Payout Operations</h2>

        <div className="fin-row2">
          <div className="fin-summary-card">
            <div className="fin-summary-icon" style={{ background: '#ecfdf5' }}>✓</div>
            <div>
              <div className="fin-summary-label">Eligible for Payout</div>
              <div className="fin-summary-val">{fmtTZS(eligible.total)}</div>
              <div className="fin-summary-sub">{eligible.count} seller{eligible.count !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        <div className="fin-ops-actions">
          <button className="fin-btn fin-btn--ghost" onClick={previewPayoutRun} disabled={previewing}>
            {previewing ? 'Previewing…' : 'Preview Payout Run'}
          </button>
          <button className="fin-btn fin-btn--ghost" onClick={syncPayouts} disabled={syncingPayouts}>
            {syncingPayouts ? 'Syncing…' : 'Sync Payouts'}
          </button>
        </div>
        <p className="fin-ops-note">
          "Preview Payout Run" shows who a real run would pay, without moving any money — it never actually
          sends payouts. A live run has to be triggered server-side (cron/worker), not from this page: ClickPesa
          allows one payout per minute, and a browser request would be disconnected by the server after 60 seconds
          for any batch of more than one seller, with no way to see how far it got.
        </p>

        {preview && (
          <div className="fin-table-wrap fin-ops-result">
            <table className="fin-table">
              <thead><tr><th>Seller ID</th><th>Available</th><th>Failures</th></tr></thead>
              <tbody>
                {(preview.sellers || []).length === 0 && (
                  <tr><td colSpan={3} className="fin-empty-cell">No sellers currently eligible.</td></tr>
                )}
                {(preview.sellers || []).map((s, i) => (
                  <tr key={s.seller_id || i}>
                    <td><code className="fin-code">{(s.seller_id || '').slice(0, 14)}…</code></td>
                    <td className="fin-green">{fmtTZS(s.available)}</td>
                    <td>{s.failures || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="fin-ops-note">
              Preview: would pay {preview.would_pay ?? 0} seller{(preview.would_pay ?? 0) !== 1 ? 's' : ''}, totalling {fmtTZS(preview.total)}.
            </p>
          </div>
        )}

        {syncResult && (
          <div className="fin-table-wrap fin-ops-result">
            <table className="fin-table">
              <thead><tr><th>Reference</th><th>Status</th><th>Restored</th><th></th></tr></thead>
              <tbody>
                {(syncResult.results || []).length === 0 && (
                  <tr><td colSpan={4} className="fin-empty-cell">Nothing needed reconciling.</td></tr>
                )}
                {(syncResult.results || []).map((r, i) => (
                  <tr key={r.reference || i}>
                    <td><code className="fin-code">{r.reference || '—'}</code></td>
                    <td>{r.ok ? (r.status || '—') : `error: ${r.error || 'unknown'}`}</td>
                    <td>{r.restored ? 'Yes' : '—'}</td>
                    <td>
                      {r.ok && r.reference && (
                        <button className="fin-btn fin-btn--ghost fin-btn--sm" onClick={() => viewPayoutDetail(r.reference)}>
                          View detail
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loading && <div className="fin-loading"><div className="mp-spinner" /> Loading…</div>}
    </div>
  );
};

export default FinancePage;
